import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { selectProducts } from '../../../redux/selectors';
import { setProducts, setSearchEdit } from '../../../redux/actions';

import StatTable from '../../base/megatable/stat-table';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import DatePicker from '../../base/date/date-picker';
import { Columns } from '../../base/megatable/column-service';
import { Loader } from '../../misc/loader-utils';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';

import linkoutIcon from '../../../images/icons/linkout.svg';
import backArrow from '../../../images/icons/small-left.png';
import ReportDropdown from '../../base/dropdowns/report-dropdown';

// Table variables
const statType = 'merchantProductsSearch';
const tableName = 'Merchant Products Search';
const sortDisabledFields = ['brand', 'recommended_product', 'product_price'];
const limit = 500;

function ArticleSearchProducts({ edit, orgId, history, match, location }) {
  // State
  const [loading, setLoading] = useState(true);
  const [rowCounts, setRowCounts] = useState({ visible: 0, all: 0 });
  const [sort, setSort] = useState({
    order_by: 'total_attributed_revenue',
    direction: 'desc',
  });

  const tableColumns = new Columns().getColumnsForTable(statType);
  const defaultColumns = cloneDeep(tableColumns);
  const [columns, setColumns] = useState(tableColumns);

  const { thirtyDay } = dateService.getPresetDateRanges();
  const [dates, setDates] = useState({
    startDate: thirtyDay.startDate,
    endDate: thirtyDay.endDate,
  });

  // Redux
  const dispatch = useDispatch();
  const startDate = dateService.formatDate(dates.startDate);
  const endDate = dateService.formatDate(dates.endDate);
  const statKey = `${edit.id},${startDate},${endDate},${sort.order_by},${sort.direction}`;
  const dataEndpoint = `/api/v0/merchants/${orgId}/stats_by_recommendation/`;
  const [products, totalItems] = useSelector(state => selectProducts(state, statKey, 1));

  // Effects
  useEffect(() => {
    if (!products.length) {
      fetchData();
    } else {
      updateRowCounts(products, totalItems);
      setLoading(false);
    }
  }, [edit, dates, sort]);

  // Methods
  async function fetchData() {
    setLoading(true);

    const params = getQueryParams();
    const resp = await api.get(dataEndpoint, params);
    const products = resp.data.data[0].stats;
    const totalItems = resp.data.data[0].page_info.total_items;

    dispatch(setProducts(products, totalItems, statKey, 1));
    updateRowCounts(products, totalItems);
    setLoading(false);
  }

  function getQueryParams(totals = false) {
    const params = {
      edit_id: edit.id,
      date_from: dateService.formatDate(dates.startDate),
      date_to: dateService.formatDate(dates.endDate),
      order_by: sort.order_by,
      direction: sort.direction,
      page: 1,
      per_page: limit,
    };

    if (totals) {
      params.group_by = 'merch_id';
    }

    return params;
  }

  function setDateRange(start, end) {
    setDates({
      startDate: start,
      endDate: end,
    });
  }

  function updateRowCounts(products, totalItems) {
    setRowCounts({
      visible: products.length,
      all: totalItems,
    });
  }

  function updateSortParams(name) {
    if (sortDisabledFields.includes(name)) return;

    const isNewColumn = sort.order_by !== name;
    setSort({
      order_by: isNewColumn ? name : sort.order_by,
      direction: isNewColumn ? 'desc' : sort.direction === 'desc' ? 'asc' : 'desc',
    });
  }

  // Render
  const table = !loading ? (
    <StatTable
      stats={products}
      columns={columns}
      sortDisabledFields={sortDisabledFields}
      updateSortParams={name => updateSortParams(name)}
      statType={statType}
      tableName={tableName}
      sortOptions={sort}
      disableEditUrl={true}
      history={history}
      match={match}
      location={location}
      classOverride="base-search-table article-search-products-table"
    />
  ) : null;

  const tableHeader = !loading ? (
    <div className="header">
      <div className="table-name">
        <span className="title">Recommended Products</span>
        <span className="edit">in "{edit.name}"</span>
        <a className="link-out" href={edit.url} target="_blank" rel="noopener noreferrer">
          <img src={linkoutIcon} alt="open article" />
        </a>
      </div>
      <div className="dropdowns">
        <DatePicker
          eventPrefix={`${tableName} Date Picker`}
          setDateRange={(start, end) =>
            setDateRange(dateService.dateFromStr(start), dateService.dateFromStr(end))
          }
          dateRange={dates}
        />
        <ColumnsDropdown
          gaEvent={{ category: 'Merchant Article Search', label: 'Columns' }}
          staticFields={[]}
          items={columns}
          updateColumns={options => setColumns(options)}
          resetColumns={() => setColumns(defaultColumns)}
          statType={statType}
        />
        <ReportDropdown
          statType={statType}
          orgId={orgId}
          gaEvent={{
            category: 'Article Search',
            label: `${tableName} Export Data`,
          }}
          queryParams={getQueryParams()}
          columns={columns}
          currentPage={1}
          perPage={50}
          rowCounts={rowCounts}
          disabled={loading}
        />
      </div>
    </div>
  ) : null;

  const backButton = !loading ? (
    <div className="back-button" onClick={() => dispatch(setSearchEdit(null))}>
      <img src={backArrow} alt="go back" />
      Back to Article Results
    </div>
  ) : null;

  return (
    <div className="result">
      <Loader loading={loading} />
      {backButton}
      {tableHeader}
      {table}
    </div>
  );
}

export default ArticleSearchProducts;
